import React from 'react';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { PromoCodeRow } from '../types';
import { AppliedToBadge } from './AppliedToBadge';
import { FilterButton } from './FilterButton';

type StatusFilter = 'all' | 'active' | 'inactive';

export function PromoCodesTable({
  promoCodeRows,
  isPromotionsLoading
}: {
  promoCodeRows: PromoCodeRow[];
  isPromotionsLoading: boolean;
}) {
  const [openActionMenu, setOpenActionMenu] = React.useState<string | null>(null);
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>('all');

  const visiblePromoCodeRows = promoCodeRows.filter((promoCode) => {
    if (statusFilter === 'active') return promoCode.isActive;
    if (statusFilter === 'inactive') return !promoCode.isActive;

    return true;
  });
  const emptyStateMessage =
    statusFilter === 'active'
      ? 'No active promo codes found'
      : statusFilter === 'inactive'
      ? 'No inactive promo codes found'
      : 'No promo codes found';

  React.useEffect(() => {
    if (!openActionMenu) return;

    function handleDocumentMouseDown(event: MouseEvent) {
      const target = event.target;

      if (!(target instanceof Element)) return;

      if (target.closest('[data-promo-action-trigger], [data-promo-action-menu]')) return;

      setOpenActionMenu(null);
    }

    document.addEventListener('mousedown', handleDocumentMouseDown);

    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
    };
  }, [openActionMenu]);

  function handleStatusFilterChange(nextStatusFilter: StatusFilter) {
    setStatusFilter(nextStatusFilter);
    setOpenActionMenu(null);
  }

  return (
    <section className="min-h-[calc(100vh-190px)] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center justify-end border-b border-gray-200 px-6 py-5">
        <div className="flex flex-wrap items-center justify-end gap-4">
          <FilterButton
            active={statusFilter === 'all'}
            onClick={() => handleStatusFilterChange('all')}
          >
            All
          </FilterButton>
          <FilterButton
            active={statusFilter === 'active'}
            onClick={() => handleStatusFilterChange('active')}
          >
            Active
          </FilterButton>
          <FilterButton
            active={statusFilter === 'inactive'}
            onClick={() => handleStatusFilterChange('inactive')}
          >
            Inactive
          </FilterButton>
        </div>
      </div>

      <div className="overflow-x-auto pb-40">
        <table className="w-full min-w-[1250px] border-separate border-spacing-0 text-left">
          <thead>
            <tr className="border-b border-gray-200 text-xl font-gilroy-bold text-neutral-700">
              <th className="border-b border-gray-200 px-10 py-7 font-semibold">Promo code</th>
              <th className="border-b border-gray-200 px-6 py-7 font-semibold">Discount value</th>
              <th className="border-b border-gray-200 px-6 py-7 font-semibold">Start date</th>
              <th className="border-b border-gray-200 px-6 py-7 font-semibold">End date</th>
              <th className="border-b border-gray-200 px-6 py-7 font-semibold">Expiry date</th>
              <th className="border-b border-gray-200 px-6 py-7 font-semibold">Applied to</th>
              <th className="border-b border-gray-200 px-6 py-7 font-semibold">Status</th>
              <th className="border-b border-gray-200 px-10 py-7 text-center font-semibold">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {isPromotionsLoading ? (
              <tr>
                <td
                  className="px-10 py-12 text-center text-xl font-gilroy-medium text-neutral-400"
                  colSpan={8}
                >
                  Loading promo codes...
                </td>
              </tr>
            ) : null}
            {!isPromotionsLoading && !visiblePromoCodeRows.length ? (
              <tr>
                <td
                  className="px-10 py-12 text-center text-xl font-gilroy-medium text-neutral-400"
                  colSpan={8}
                >
                  {emptyStateMessage}
                </td>
              </tr>
            ) : null}
            {!isPromotionsLoading &&
              visiblePromoCodeRows.map((promoCode) => (
                <tr key={promoCode.code} className="text-2xl font-gilroy-medium text-neutral-700">
                  <td className="whitespace-nowrap px-10 py-9 align-top">{promoCode.code}</td>
                  <td className="whitespace-nowrap px-6 py-9 align-top">
                    {promoCode.discountValue}
                  </td>
                  <td className="whitespace-nowrap px-6 py-9 align-top">{promoCode.startDate}</td>
                  <td className="whitespace-nowrap px-6 py-9 align-top">{promoCode.endDate}</td>
                  <td className="whitespace-nowrap px-6 py-9 align-top">{promoCode.expiryDate}</td>
                  <td className="px-6 py-8 align-top">
                    <div className="flex flex-wrap items-center gap-3">
                      {promoCode.appliedTo.map((item) => (
                        <AppliedToBadge key={item}>{item}</AppliedToBadge>
                      ))}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-8 align-top">
                    <span
                      className={`inline-flex items-center rounded-xl px-6 py-3 text-lg font-gilroy-bold leading-none shadow-sm ring-1 ${
                        promoCode.isActive
                          ? 'bg-emerald-50 text-emerald-700 ring-emerald-100'
                          : 'bg-rose-50 text-rose-600 ring-rose-100'
                      }`}
                    >
                      {promoCode.status}
                    </span>
                  </td>
                  <td className="relative px-10 py-7 text-center align-top">
                    <button
                      data-promo-action-trigger
                      type="button"
                      aria-label={`Open actions for ${promoCode.code}`}
                      aria-expanded={openActionMenu === promoCode.code}
                      className="inline-flex h-11 w-11 items-center justify-center rounded-full text-neutral-900 transition hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                      onClick={() =>
                        setOpenActionMenu((currentOpenMenu) =>
                          currentOpenMenu === promoCode.code ? null : promoCode.code
                        )
                      }
                    >
                      <MoreVertIcon className="!h-7 !w-7" />
                    </button>

                    {openActionMenu === promoCode.code ? (
                      <div
                        data-promo-action-menu
                        className="absolute right-7 top-20 z-30 w-[260px] rounded-xl border border-gray-100 bg-white p-2 text-left shadow-[0_12px_28px_rgba(15,23,42,0.12)]"
                      >
                        <button
                          type="button"
                          className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-lg font-gilroy-bold text-red-500 transition hover:bg-red-50 hover:text-red-600 focus:outline-none focus-visible:bg-red-50 focus-visible:ring-2 focus-visible:ring-red-100"
                        >
                          <DeleteOutlineIcon className="!h-6 !w-6" />
                          <span>Delete promo code</span>
                        </button>
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
